<template>
  <teleport v-if="!isPopup" to="body">
    <div v-if="isActive" class="tour">
      <!-- Click-capturing dim. Clicking it is a deliberate no-op: "advance" would let people click
           through the walkthrough by accident, and "dismiss" would permanently consume it. -->
      <div class="tour__backdrop" @click.stop @wheel.prevent />

      <div
        ref="cardRef"
        class="tour__card"
        :class="{ 'tour__card--welcome': view === 'welcome', 'tour__card--outro': isOutro }"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        :aria-label="t('onboarding.aria.dialog')"
        aria-labelledby="tour-card-title"
        aria-describedby="tour-card-text"
        @keydown="onCardKeydown"
      >
        <!-- ── welcome ───────────────────────────────────────────────────── -->
        <template v-if="view === 'welcome'">
          <!-- Over the brand panel rather than in a bar of its own: the hero already owns the top of
               this card, and a bar above it would push the one full-bleed thing off the edge. -->
          <v-btn
            class="tour__close tour__close--hero"
            icon="mdi-close"
            variant="text"
            size="small"
            density="comfortable"
            :aria-label="t('onboarding.action.skip')"
            @click="skip"
          />
          <div class="tour__hero">
            <lew-logo class="tour__hero-logo" icon />
          </div>
          <h2 id="tour-card-title" class="tour__welcome-title">
            {{ t("onboarding.welcome.title", { name: authStore.firstName }) }}
          </h2>
          <p id="tour-card-text" class="tour__welcome-text">{{ t("onboarding.welcome.text") }}</p>
          <p class="tour__welcome-meta">
            {{ t("onboarding.welcome.length", { count: steps.length, minutes: estimatedMinutes }) }}
          </p>
          <div class="tour__foot tour__foot--welcome">
            <v-btn variant="text" @click="skip">
              {{ t("onboarding.action.later") }}
            </v-btn>
            <v-spacer />
            <v-btn ref="primaryRef" color="primary" variant="flat" @click="beginSteps">
              {{ t("onboarding.action.begin") }}
            </v-btn>
          </div>
        </template>

        <!-- ── the walkthrough ───────────────────────────────────────────── -->
        <template v-else-if="view === 'steps' && step">
          <div class="tour__bar">
            <v-icon v-if="chapter" class="tour__icon" :icon="chapter.icon" size="20" />
            <span class="tour__eyebrow">{{ chapterLabel }}</span>
            <v-spacer />
            <v-btn
              class="tour__close"
              icon="mdi-close"
              variant="text"
              size="small"
              density="comfortable"
              :aria-label="t('onboarding.action.skip')"
              @click="skip"
            />
          </div>

          <!-- Re-keyed per step so the element is rebuilt: a <video> whose src is swapped keeps the
               old frame until the new one decodes, which reads as the wrong clip being shown. -->
          <lew-tour-clip
            v-if="!videoFailed"
            :key="step.video"
            v-model:rate="playbackRate"
            class="tour__stage"
            :src="clipUrl(step.video)"
            :poster="posterUrl(step.video)"
            :label="title"
            :reduced-motion="reducedMotion"
            @error="videoFailed = true"
          />
          <!-- A missing or unplayable clip must not leave a hole where the explanation was. -->
          <div v-else class="tour__stage tour__stage--empty">
            <v-icon icon="mdi-play-box-outline" size="40" />
          </div>

          <h2 id="tour-card-title" class="tour__title">{{ title }}</h2>
          <p id="tour-card-text" class="tour__text">{{ text }}</p>

          <!-- One segment per chapter, filled by how far into it we are: seven steps in six groups
               reads as "where am I in the product", which a flat 4/7 does not. -->
          <div class="tour__progress-bar" aria-hidden="true">
            <span v-for="entry in visibleChapters" :key="entry.key" class="tour__pip">
              <span class="tour__pip-fill" :style="{ width: `${chapterProgress(entry.key)}%` }" />
            </span>
          </div>

          <div class="tour__foot">
            <span class="tour__progress">
              {{ t("onboarding.progress", { current: stepIndex + 1, total: steps.length }) }}
            </span>
            <v-spacer />
            <v-btn variant="text" @click="previous">
              {{ t("onboarding.action.back") }}
            </v-btn>
            <v-btn ref="primaryRef" color="primary" variant="flat" class="ml-2" @click="next">
              {{ isLast ? t("onboarding.action.finishSteps") : t("onboarding.action.next") }}
            </v-btn>
          </div>
        </template>

        <!-- ── outro ─────────────────────────────────────────────────────── -->
        <template v-else>
          <div class="tour__bar">
            <v-icon class="tour__icon" icon="mdi-check-circle-outline" size="20" />
            <span class="tour__eyebrow">{{ t("onboarding.outro.eyebrow") }}</span>
            <v-spacer />
            <v-btn
              class="tour__close"
              icon="mdi-close"
              variant="text"
              size="small"
              density="comfortable"
              :aria-label="t('onboarding.action.skip')"
              @click="skip"
            />
          </div>
          <h2 id="tour-card-title" class="tour__title">{{ t("onboarding.outro.title") }}</h2>
          <p id="tour-card-text" class="tour__text">{{ t("onboarding.outro.text") }}</p>

          <!-- The route back in, performed rather than described: the real help button being
               clicked and the real menu dropping open on the real entry. It opens on the whole app
               and then pushes in on that corner of the toolbar, so the thing to look for fills the
               frame instead of sitting in it forty pixels wide. -->
          <figure v-if="!hintFailed" class="tour__hint">
            <!-- No seek or speed buttons here: this one is a nine-second aside on a screen that is
                 finished, and controls on it invite driving something nobody needs to drive. The
                 speed chosen during the walkthrough still carries over. -->
            <lew-tour-clip
              v-model:rate="playbackRate"
              class="tour__stage"
              hide-controls
              :src="clipUrl(ONBOARDING_OUTRO_VIDEO)"
              :poster="posterUrl(ONBOARDING_OUTRO_VIDEO)"
              :label="t('onboarding.outro.hint')"
              :reduced-motion="reducedMotion"
              @error="hintFailed = true"
            />
            <figcaption class="tour__hint-caption">{{ t("onboarding.outro.hint") }}</figcaption>
          </figure>

          <!-- Spelled out when the clip cannot be played at all. -->
          <div v-else class="tour__recipe">
            <span class="tour__recipe-step">
              <v-icon icon="mdi-help-circle-outline" size="18" />
              {{ t("toolbar.help.label") }}
            </span>
            <v-icon icon="mdi-chevron-right" size="16" class="tour__recipe-arrow" />
            <span class="tour__recipe-step">
              <v-icon icon="mdi-compass-outline" size="18" />
              {{ t("toolbar.help.tour") }}
            </span>
          </div>

          <!-- Individual features the tenant lacks are hidden entirely, never shown as locked
               teasers; this one generic line is the only place any of them is mentioned. -->
          <div v-if="showUpsell" class="tour__upsell">
            <v-icon icon="mdi-plus-box-outline" size="18" class="tour__upsell-icon" />
            <div class="tour__upsell-body">
              <span>{{ t("onboarding.upsell.text") }}</span>
              <v-btn
                v-if="canViewBilling"
                class="tour__upsell-cta"
                variant="text"
                color="primary"
                size="small"
                density="comfortable"
                @click="goToPlans"
              >
                {{ t("onboarding.upsell.cta") }}
              </v-btn>
            </div>
          </div>

          <div class="tour__foot">
            <v-btn v-if="steps.length" variant="text" @click="previous">
              {{ t("onboarding.action.back") }}
            </v-btn>
            <!-- Straight to step one, not to the welcome screen: being greeted a second time is odd.
                 A quiet text button on purpose — the clip above it is what teaches where the tour
                 lives, and a prominent replay here would train the button instead of the place. -->
            <v-btn v-if="steps.length" variant="text" prepend-icon="mdi-restart" @click="beginSteps">
              {{ t("onboarding.action.replay") }}
            </v-btn>
            <v-spacer />
            <v-btn ref="primaryRef" color="primary" variant="flat" @click="finish">
              {{ t("onboarding.action.done") }}
            </v-btn>
          </div>
        </template>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
/**
 * @fileOverview The onboarding walkthrough's overlay.
 *
 * Mounted once in App.vue — the only truly app-global mount point, since LewBaseLayout is remounted by
 * every view on every navigation. State lives in useOnboardingTour; this component only renders it.
 *
 * A centred modal with no anchors into the page. The previous version spotlit real elements and had
 * to know where the navigation drawer was, which it could not below the overlay breakpoint — the
 * drawer is off-canvas there, so every navigation step pointed at empty screen. A modal playing a
 * recording has no geometry to get wrong, and can show a filled-in report on a tenant that has none.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { auth } from "@/services/auth"
import { PERMISSIONS } from "@/services/permission"
import { hasPermission } from "@/services/permissionService"
import { useAuthStore } from "@/store/authentication"
import { useOnboardingTour } from "@/composables/useOnboardingTour"
import LewTourClip from "./LewTourClip.vue"
import { ONBOARDING_OUTRO_VIDEO, ONBOARDING_VIDEO_BASE } from "@/services/onboardingTour"

/** Roughly how long a viewer spends on one step, for the welcome screen's estimate. */
const SECONDS_PER_STEP = 20

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const {
  isActive,
  view,
  steps,
  step,
  stepIndex,
  chapter,
  visibleChapters,
  isLast,
  missingFeatureSets,
  autoStartAttempted,
  start,
  beginSteps,
  next,
  previous,
  skip,
  finish,
  deactivate,
} = useOnboardingTour()

const cardRef = ref<HTMLElement | null>(null)
const primaryRef = ref<{ $el: HTMLElement } | null>(null)
const videoFailed = ref(false)
const hintFailed = ref(false)
/**
 * Sticky across steps and into the closing screen: someone who sped one clip up wants the next one
 * sped up too. Owned here rather than in `LewTourClip`, which is rebuilt on every step.
 */
const playbackRate = ref(1)

const clipUrl = (name: string) => `${ONBOARDING_VIDEO_BASE}/${name}.mp4`
const posterUrl = (name: string) => `${ONBOARDING_VIDEO_BASE}/${name}.jpg`

// The personnel-planning pop-out window runs the same SPA; the walkthrough must never appear there.
const isPopup = computed(() => route.query.popup === "true")

/**
 * Autoplaying video is motion the user did not ask for. Under `prefers-reduced-motion` the clip is
 * held on its poster with native controls instead, so the content is still reachable.
 */
const reducedMotion =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false

// ── warming the clips ───────────────────────────────────────────────────────

/**
 * Clips already pulled into the browser's media cache, so a step never pays for the same file twice.
 * Module-scope rather than component-scope: replaying the walkthrough from the help menu should not
 * start the downloads over.
 */
const warmed = new Set<string>()
/** Holds the detached loaders alive — a `<video>` dropped mid-fetch can be collected. */
const loaders: HTMLVideoElement[] = []

/**
 * Starts downloading a clip before anything asks to play it.
 *
 * `preload="auto"` on the visible element only begins once that step is on screen, which meant every
 * step opened on its poster and then jumped into motion a beat later. Warming the *next* clip while
 * the current one plays — and the *first* one while the welcome screen is being read — means the file
 * is already in cache by the time the element that plays it exists.
 *
 * A detached element loads perfectly well; it never has to be in the document.
 */
const warmClip = (name?: string) => {
  if (!name || typeof document === "undefined") return
  const url = clipUrl(name)
  if (warmed.has(url)) return
  warmed.add(url)

  const loader = document.createElement("video")
  loader.preload = "auto"
  loader.muted = true
  loader.src = url
  loaders.push(loader)
  const release = () => {
    loader.removeEventListener("canplaythrough", release)
    loader.removeEventListener("error", release)
    loaders.splice(loaders.indexOf(loader), 1)
  }
  loader.addEventListener("canplaythrough", release)
  // A clip that fails to warm is not an error here: the visible element will try again and show the
  // fallback tile if it fails too.
  loader.addEventListener("error", release)
  loader.load()
}

/**
 * The clip after this one, which is the one worth having ready. On the last step that is the outro's
 * hint clip, so the closing screen opens in motion like every screen before it.
 */
const warmNext = () => warmClip(steps.value[stepIndex.value + 1]?.video ?? ONBOARDING_OUTRO_VIDEO)

const title = computed(() => (step.value ? t(`onboarding.step.${step.value.key}.title`) : ""))
const text = computed(() => (step.value ? t(`onboarding.step.${step.value.key}.text`) : ""))
const chapterLabel = computed(() => (chapter.value ? t(`onboarding.chapter.${chapter.value.key}`) : ""))

const estimatedMinutes = computed(() => Math.max(1, Math.round((steps.value.length * SECONDS_PER_STEP) / 60)))

const canViewBilling = hasPermission(PERMISSIONS.VIEW_BILLING)
const showUpsell = computed(() => missingFeatureSets.value.length > 0)

/** Mirrors the closing screen's `v-else`, which also catches a walkthrough that gated down to nothing. */
const isOutro = computed(() => view.value !== "welcome" && (view.value === "outro" || !step.value))

/** How much of one chapter's segment is filled: 0 before it, 100 after, proportional inside it. */
const chapterProgress = (key: string) => {
  const inChapter = steps.value.filter(s => s.chapter === key)
  const done = inChapter.filter(s => steps.value.indexOf(s) <= stepIndex.value).length
  return inChapter.length ? (done / inChapter.length) * 100 : 0
}

const goToPlans = () => {
  finish()
  router.push({ name: "ChangePlan" })
}

// ── interaction ─────────────────────────────────────────────────────────────

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    event.preventDefault()
    skip()
  } else if (view.value !== "steps") {
    // Arrow keys only mean something while stepping.
  } else if (event.key === "ArrowRight") {
    next()
  } else if (event.key === "ArrowLeft") {
    previous()
  }
}

/** Keeps focus inside the card while it is open. */
const onCardKeydown = (event: KeyboardEvent) => {
  if (event.key !== "Tab") return
  const focusable = cardRef.value?.querySelectorAll<HTMLElement>("button:not([disabled])")
  if (!focusable?.length) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

// ── lifecycle ───────────────────────────────────────────────────────────────

let previouslyFocused: HTMLElement | null = null

const focusCard = async () => {
  await nextTick()
  if (primaryRef.value?.$el) primaryRef.value.$el.focus?.()
  else cardRef.value?.focus?.()
}

watch(isActive, async active => {
  if (active) {
    previouslyFocused = document.activeElement as HTMLElement | null
    window.addEventListener("keydown", onKeydown)
    // The welcome screen is several seconds of reading. Spending them downloading the first clip is
    // the difference between the walkthrough opening in motion and opening on a still.
    warmClip(steps.value[0]?.video)
    await focusCard()
  } else {
    window.removeEventListener("keydown", onKeydown)
    previouslyFocused?.focus?.()
    previouslyFocused = null
  }
})

watch([stepIndex, view], async () => {
  if (!isActive.value) return
  // A clip that failed once must not condemn the rest of the walkthrough to the fallback tile.
  videoFailed.value = false
  if (view.value === "steps") warmNext()
  await focusCard()
})

// Navigating away mid-walkthrough closes it WITHOUT recording it as seen, so an accidental exit
// (browser back, a 402 redirect) does not consume the user's single automatic run.
watch(
  () => route.fullPath,
  () => {
    if (isActive.value) deactivate()
  }
)

/**
 * Auto-start for users who have not seen it. Deliberately not gated on the subscription store: its
 * status call needs VIEW_BILLING, which a customer-edited role can be missing — entitlements come
 * from /principal, which every authenticated user may read.
 */
const maybeAutoStart = () => {
  if (autoStartAttempted.value || isActive.value) return
  if (isPopup.value || route.name !== "Dashboard") return
  if (!auth.isLoggedIn() || authStore.onboardingCompleted !== false) return
  autoStartAttempted.value = true
  start()
}

watch(
  () => [route.name, authStore.onboardingCompleted] as const,
  () => maybeAutoStart(),
  { immediate: true }
)

onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown))
</script>

<style scoped>
/* Scoped styles survive the teleport: the data-v attribute is stamped on these elements themselves.
   (Precedent: GeneralContextMenu.)

   z-index: the app bar and navigation drawer sit at 1000–1004 (Vuetify ROOT_ZINDEX + layout items) and
   Vuetify overlays start at 2000, so 2500+ paints above an open menu too, while staying below the
   personnel-planning context menus at 9998. */
.tour__backdrop {
  position: fixed;
  inset: 0;
  z-index: 2500;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(2px);
}

.tour__card {
  position: fixed;
  z-index: 2502;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Wide enough that a 1280px-wide recording of the app is still legible inside it — at 680px the
     labels in the clip were a smear. The welcome and outro screens override this downwards; they
     are text, and a 60-character measure is easier to read than a 100-character one. */
  width: 820px;
  max-width: calc(100vw - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  /* A variable so the clip can cancel it exactly and run edge to edge. */
  --tour-pad: 24px;
  padding: var(--tour-pad);
  border-radius: 18px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  box-shadow: 0 18px 56px rgba(0, 0, 0, 0.38);
  animation: tour-in 0.3s cubic-bezier(0.2, 0.8, 0.3, 1) both;
}

@keyframes tour-in {
  from {
    opacity: 0;
    transform: translate(-50%, -46%) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

/* Sticky, because the card scrolls on a short viewport and a close button that scrolls out of reach
   is the one control that must never do that. Full-bleed and opaque so the clip passes under it
   cleanly rather than showing through. */
.tour__bar {
  position: sticky;
  top: calc(-1 * var(--tour-pad));
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: calc(-1 * var(--tour-pad)) calc(-1 * var(--tour-pad)) 0;
  padding: 10px var(--tour-pad) 10px calc(var(--tour-pad) - 2px);
  background: rgb(var(--v-theme-surface));
  border-radius: 18px 18px 0 0;
}

.tour__close {
  color: rgba(var(--v-theme-on-surface), 0.55);
  margin-right: -8px;
}

/* The welcome screen has no bar — its close sits on the brand panel, where the surface colour would
   disappear. */
.tour__close--hero {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  color: rgba(255, 255, 255, 0.85);
}

/* The two text screens narrow: a sentence reads badly across 800 pixels. Only the walkthrough needs
   the full width, and only because a recording of the whole app is in it. The closing screen is a
   confirmation that happens to carry a small clip, not a clip with text around it, so it sits
   between the two. */
.tour__card--welcome {
  width: 560px;
}

.tour__card--outro {
  width: 640px;
}

/* ── welcome ──────────────────────────────────────────────────────────────── */

.tour__card--welcome {
  text-align: center;
  padding: 8px 32px 24px;
}

/* The brand mark on its own field of colour: this is the one screen in the product that exists to
   say "you're in", and a plain white dialog says nothing at all. */
.tour__hero {
  margin: -8px -32px 20px;
  padding: 34px 0 30px;
  border-radius: 18px 18px 0 0;
  background:
    radial-gradient(120% 140% at 50% 0%, rgba(var(--v-theme-accent), 0.28), transparent 62%),
    rgb(var(--v-theme-primary));
}

.tour__hero-logo {
  width: 62px;
  height: 62px;
  margin: 0 auto;
}

.tour__welcome-title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 10px;
}

.tour__welcome-text {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(var(--v-theme-on-surface), 0.78);
}

.tour__welcome-meta {
  margin: 14px 0 0;
  font-size: 0.8125rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.tour__foot--welcome {
  margin-top: 22px;
}

/* ── the clip ─────────────────────────────────────────────────────────────── */

/* The clip's own frame and controls live in LewTourClip; this is only where it sits in the card.
   Edge to edge, cancelling the card's padding: a 16:9 recording inset on all four sides reads as a
   thumbnail pasted into a document, and the extra ~50px of width is width the app inside it needs.
   Two classes deep so it beats the child's own single-class rounding.

   Direct children only, which means the walkthrough's clip. The closing screen's sits inside a
   <figure> and deliberately keeps LewTourClip's own inset, rounded frame — see .tour__hint. */
.tour__card > .tour__stage {
  margin: 0 calc(-1 * var(--tour-pad)) 18px;
  border-radius: 0;
  border-left: 0;
  border-right: 0;
}

/* Stands in for a clip that could not be played, at the same size so the card does not resize. */
.tour__card > .tour__stage--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  color: rgba(var(--v-theme-on-surface), 0.35);
}

/* ── text ─────────────────────────────────────────────────────────────────── */

.tour__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.tour__icon {
  color: rgb(var(--v-theme-primary));
}

.tour__eyebrow {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
}

.tour__title {
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 6px;
}

.tour__text {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: rgba(var(--v-theme-on-surface), 0.75);
}

/* ── outro ────────────────────────────────────────────────────────────────── */

/* Centred, on a short measure: the closing screen is a "you're done" confirmation, and left-aligned
   prose running the full width of the card is the shape of the seven teaching screens before it. */
.tour__card--outro .tour__title,
.tour__card--outro .tour__text {
  max-width: 46ch;
  margin-inline: auto;
  text-align: center;
}

/* The clip here is an aside, not the subject — where to find the tour again, shown rather than
   described. So it is a small inset panel with its own frame rather than the walkthrough's
   full-bleed stage, which at this card's width dominates the screen and reads as an eighth step.
   It survives the smaller size because it pushes in to 2.6x on the help button (the zoom is baked
   into the file; see marketing/capture-onboarding.mjs): the menu it opens is still legible here,
   and the wide establishing beat before it only has to say "top right". */
.tour__hint {
  margin: 20px auto 0;
  max-width: 460px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tour__hint-caption {
  font-size: 0.8125rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  text-align: center;
}

/* Stands where the clip would have stood, so it inherits the same centred inset. */
.tour__recipe {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 20px auto 0;
  max-width: 460px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.05);
}

.tour__recipe-step {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 600;
}

.tour__recipe-arrow {
  color: rgba(var(--v-theme-on-surface), 0.45);
}

/* Same column as the clip above it. Its own text stays left-aligned — it is a notice, and two
   centred lines wrapping under an icon read as a caption. */
.tour__upsell {
  display: flex;
  gap: 8px;
  margin: 16px auto 0;
  max-width: 460px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.07);
  font-size: 0.8125rem;
  line-height: 1.45;
  text-align: left;
}

.tour__upsell-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: rgb(var(--v-theme-primary));
}

.tour__upsell-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

/* Shrink-wrapped and pulled flush left: as a block-level button it stretched the full width of the
   notice and centred its label, which read as a stray centred line under left-aligned text. The
   negative margin cancels the button's own inline padding so the label starts on the same vertical
   line as the sentence above it. */
.tour__upsell-cta {
  margin-top: 2px;
  margin-left: -8px;
  padding-inline: 8px;
}

/* ── progress & footer ────────────────────────────────────────────────────── */

.tour__progress-bar {
  display: flex;
  gap: 4px;
  margin-top: 18px;
}

.tour__pip {
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: rgba(var(--v-theme-on-surface), 0.14);
  overflow: hidden;
}

.tour__pip-fill {
  display: block;
  height: 100%;
  border-radius: 2px;
  background: rgb(var(--v-theme-primary));
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* The walkthrough is driven from these two buttons, so they are full-size rather than the compact
   density the rest of the card uses. At `comfortable` they were 28px tall — findable, but small for
   the control someone presses seven times in a row, and smaller than the same pair on any other
   dialog in the app. */
.tour__foot {
  display: flex;
  align-items: center;
  margin-top: 16px;
  flex-wrap: wrap;
  row-gap: 6px;
}

.tour__progress {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

@media (max-width: 600px) {
  .tour__card {
    --tour-pad: 18px;
  }

  .tour__card--welcome {
    padding: 8px 20px 18px;
  }

  .tour__hero {
    margin: -8px -20px 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tour__card,
  .tour__pip-fill {
    animation: none;
    transition: none;
  }
}
</style>
